import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';

import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import { BULK_APPLY_SCOPE } from '../utils/bulkApplyPlan';
import { REVIEW_GROUP, REVIEW_GROUP_TITLE, reviewGroupFor } from '../utils/bulkLotNdcReviewGroups';
import { ROW_STATUS, formatQty } from '../utils/bulkLotNdcStatus';
import { STATUS_COLOR, statusLabelFor } from '../utils/bulkLotNdcStatusLabels';
import { warningTextTone, warningTint, warningTone } from '../utils/warningTone';
import ChangeNdcLotBulkApplyFacilityNote from './ChangeNdcLotBulkApplyFacilityNote';

/**
 * POM-6537 — "Also apply this lot to the other rows" in Change NDC & lot.
 *
 * Laid out so the two options are read first and the rest after: the checkbox, the options, the
 * system deficit the whole Apply records, then one line saying how many rows will get the lot. The
 * rows themselves open behind Show more, each ticked, for the operator to untick what should stay.
 */

const deficitWord = SYSTEM_DEFICIT.toLowerCase();
const LIST_ID = 'bulk-apply-rows';

const recordedLotText = (item) => (item.recordedLots ?? []).map((lot) => lot.lotNumber).join(', ');

/** What happens to one listed row, in the words of the row it is now. */
const describeEntry = ({ entry, lotNumber }) => {
  const { row, isTicked, deficitQty } = entry;
  const current = recordedLotText(row.item);
  if (!isTicked) {
    return current ? `Keeps ${current} (you unticked it)` : 'Stays without a lot (you unticked it)';
  }

  const lead =
    reviewGroupFor(row.status) === REVIEW_GROUP.NEEDS_ATTENTION
      ? `${statusLabelFor(row)} → ${lotNumber}`
      : `Moves from ${current} to ${lotNumber}`;
  const quantity = Number(row.item.fillQuantity ?? 0);
  if (!deficitQty) return lead;
  if (deficitQty >= quantity) return `${lead} · all ${formatQty(quantity)} as ${deficitWord}`;

  return `${lead} · ${formatQty(quantity - deficitQty)} from stock + ${formatQty(
    deficitQty
  )} as ${deficitWord}`;
};

const ENTRY_CHIP = {
  CHANGE: { label: 'Change', sx: { color: STATUS_COLOR[ROW_STATUS.CHANGED] } },
  CHANGE_AND_CLAIM: {
    label: 'Change + claim',
    sx: {
      color: warningTextTone,
      borderColor: (theme) => warningTint(theme, 0.6),
      bgcolor: (theme) => warningTint(theme, 0.12),
    },
  },
  KEEP: { label: 'Keep', sx: { color: 'text.secondary', borderColor: 'divider' } },
};

const chipFor = ({ isTicked, deficitQty }) => {
  if (!isTicked) return ENTRY_CHIP.KEEP;

  return deficitQty > 0 ? ENTRY_CHIP.CHANGE_AND_CLAIM : ENTRY_CHIP.CHANGE;
};

const GroupName = ({ group }) => (
  <Chip
    size='small'
    label={REVIEW_GROUP_TITLE[group]}
    sx={{
      fontWeight: 700,
      color: 'primary.main',
      bgcolor: 'grey.100',
      border: '1px solid',
      borderColor: 'divider',
    }}
  />
);

GroupName.propTypes = { group: PropTypes.oneOf(Object.values(REVIEW_GROUP)).isRequired };

const SCOPE_OPTIONS = [
  {
    scope: BULK_APPLY_SCOPE.NEEDS_ATTENTION,
    lead: 'Only rows in',
    group: REVIEW_GROUP.NEEDS_ATTENTION,
  },
  {
    scope: BULK_APPLY_SCOPE.ALL_ROWS,
    lead: 'All rows, including',
    group: REVIEW_GROUP.NO_ACTION_NEEDED,
  },
];

// Compact outlined options, side by side: the picked one is ringed, so the choice reads at a
// glance without the options outweighing the rows they decide.
const ScopeOptions = ({ scope, onChange }) => (
  <RadioGroup
    row
    aria-label='Which rows'
    value={scope}
    onChange={(event) => onChange(event.target.value)}
    sx={{ gap: 1.25 }}
  >
    {SCOPE_OPTIONS.map((option) => {
      const isSelected = option.scope === scope;

      return (
        <FormControlLabel
          key={option.scope}
          value={option.scope}
          control={<Radio size='small' />}
          label={
            <Box
              component='span'
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, typography: 'body2' }}
            >
              {option.lead}
              <GroupName group={option.group} />
            </Box>
          }
          sx={{
            m: 0,
            pl: 0.5,
            pr: 1.5,
            py: 0.25,
            minWidth: 0,
            border: '1px solid',
            borderColor: isSelected ? 'primary.main' : 'divider',
            borderRadius: 2,
            // The second pixel of the ring is a shadow, so picking an option does not resize it.
            boxShadow: (theme) =>
              isSelected ? `inset 0 0 0 1px ${theme.palette.primary.main}` : 'none',
            bgcolor: (theme) =>
              isSelected ? alpha(theme.palette.primary.main, 0.06) : theme.palette.background.paper,
          }}
        />
      );
    })}
  </RadioGroup>
);

ScopeOptions.propTypes = {
  scope: PropTypes.oneOf(Object.values(BULK_APPLY_SCOPE)).isRequired,
  onChange: PropTypes.func.isRequired,
};

// The list below holds only the other rows, so the edited row is named apart from them.
const describeShortRows = ({ isEditedRowShort, otherShortCount }) => {
  if (otherShortCount === 0) return 'this row';
  if (!isEditedRowShort) return `${otherShortCount} of these rows`;

  return `this row and ${otherShortCount} of the others`;
};

const DeficitBanner = ({ entries, lotNumber, isManualLot, editedRowDeficitQty }) => {
  const shortEntries = entries.filter((entry) => entry.isTicked && entry.deficitQty > 0);
  const totalQty = shortEntries.reduce((sum, entry) => sum + entry.deficitQty, editedRowDeficitQty);
  if (totalQty <= 0) return null;

  const lead = isManualLot
    ? `Lot ${lotNumber} is recorded by hand, so every row on it is claimed in full: `
    : `Lot ${lotNumber} cannot cover ${describeShortRows({
        isEditedRowShort: editedRowDeficitQty > 0,
        otherShortCount: shortEntries.length,
      })}, so `;

  return (
    <Alert
      severity='warning'
      icon={<WarningAmberIcon fontSize='inherit' />}
      sx={{
        color: warningTextTone,
        bgcolor: (theme) => warningTint(theme, 0.12),
        border: '1px solid',
        borderColor: (theme) => warningTint(theme, 0.4),
        '& .MuiAlert-icon': { color: warningTone },
      }}
    >
      <Typography variant='body2' sx={{ color: warningTextTone }}>
        {lead}
        <Box component='span' fontWeight={700}>
          {`${formatQty(totalQty)} will be recorded as ${deficitWord}`}
        </Box>
        . Apply to confirm.
      </Typography>
    </Alert>
  );
};

DeficitBanner.propTypes = {
  entries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  lotNumber: PropTypes.string,
  isManualLot: PropTypes.bool,
  editedRowDeficitQty: PropTypes.number,
};

DeficitBanner.defaultProps = { lotNumber: '', isManualLot: false, editedRowDeficitQty: 0 };

const EntryRow = ({ entry, lotNumber, onToggle }) => {
  const { item } = entry.row;
  const chip = chipFor(entry);

  return (
    <Box
      component='label'
      sx={{
        display: { xs: 'flex', sm: 'grid' },
        flexWrap: 'wrap',
        gridTemplateColumns: '42px 96px max-content 64px 132px minmax(0, 1fr)',
        alignItems: 'center',
        columnGap: 1.5,
        rowGap: 0.5,
        px: 1,
        py: 1,
        cursor: 'pointer',
        '&:not(:last-of-type)': { borderBottom: '1px solid', borderColor: 'divider' },
      }}
    >
      <Checkbox
        checked={entry.isTicked}
        onChange={(event) => onToggle(item.requestLineItemId, event.target.checked)}
        inputProps={{ 'aria-label': `Apply lot ${lotNumber} to Rx ${item.rxNumber ?? ''}` }}
      />
      <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
        {`Rx ${item.rxNumber ?? ''}`}
      </Typography>
      <Typography variant='body2'>{item.fulfillingPharmacyName ?? ''}</Typography>
      <Typography variant='body2'>{`Qty ${formatQty(item.fillQuantity)}`}</Typography>
      <Chip
        size='small'
        variant='outlined'
        label={chip.label}
        sx={{ fontWeight: 600, borderColor: 'currentColor', ...chip.sx }}
      />
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ minWidth: 0, flexBasis: { xs: '100%', sm: 'auto' }, pl: { xs: 6.5, sm: 0 } }}
      >
        {describeEntry({ entry, lotNumber })}
      </Typography>
    </Box>
  );
};

EntryRow.propTypes = {
  entry: PropTypes.shape({
    row: PropTypes.shape({
      item: PropTypes.shape({
        requestLineItemId: PropTypes.string,
        rxNumber: PropTypes.string,
        fulfillingPharmacyName: PropTypes.string,
        fillQuantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    }),
    isTicked: PropTypes.bool,
    deficitQty: PropTypes.number,
  }).isRequired,
  lotNumber: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
};

EntryRow.defaultProps = { lotNumber: '' };

const EntryList = ({ bulkApply }) => {
  const { entries, lotNumber, isManualLot, isListOpen } = bulkApply;
  if (entries.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        {`No other row here can take lot ${lotNumber}.`}
      </Typography>
    );
  }

  const tickedCount = entries.filter((entry) => entry.isTicked).length;
  const lotLabel = `lot ${lotNumber}${isManualLot ? ' (recorded by hand)' : ''}`;
  const countText =
    tickedCount === entries.length
      ? `${tickedCount} row${tickedCount === 1 ? '' : 's'} will get ${lotLabel}.`
      : `${tickedCount} of ${entries.length} rows will get ${lotLabel}.`;
  let hint = 'Untick any you want to leave as they are.';
  if (!isListOpen) {
    hint =
      tickedCount === entries.length
        ? 'To choose only some of them, click Show more.'
        : 'To change which ones, click Show more.';
  }

  return (
    <>
      <Stack direction='row' alignItems='center' sx={{ gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant='body2' color='text.secondary' sx={{ flex: '1 1 260px', minWidth: 0 }}>
          <Box component='span' sx={{ fontWeight: 700, color: 'text.primary' }}>
            {countText}
          </Box>{' '}
          {hint}
        </Typography>
        <Button
          size='small'
          variant='outlined'
          aria-expanded={isListOpen}
          aria-controls={LIST_ID}
          onClick={bulkApply.toggleList}
        >
          {isListOpen ? 'Show less' : 'Show more'}
        </Button>
      </Stack>
      {isListOpen && (
        <Box
          id={LIST_ID}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {/* Deliberately not A–Z: the order bulkApplyPlan shares the stock out in, so the rows
              that get it are read first. */}
          {entries.map((entry) => (
            <EntryRow
              key={entry.row.item.requestLineItemId}
              entry={entry}
              lotNumber={lotNumber}
              onToggle={bulkApply.setRowTicked}
            />
          ))}
        </Box>
      )}
    </>
  );
};

const BULK_APPLY_SHAPE = PropTypes.shape({
  otherRowCount: PropTypes.number,
  isApplied: PropTypes.bool,
  scope: PropTypes.oneOf(Object.values(BULK_APPLY_SCOPE)),
  isListOpen: PropTypes.bool,
  entries: PropTypes.arrayOf(PropTypes.shape({})),
  lotNumber: PropTypes.string,
  isManualLot: PropTypes.bool,
  editedFacilityName: PropTypes.string,
  tickedRowsByOtherFacility: PropTypes.arrayOf(PropTypes.shape({})),
  rowsLeftOutByFacility: PropTypes.arrayOf(PropTypes.shape({})),
  editedRowDeficitQty: PropTypes.number,
  setTicked: PropTypes.func,
  setScope: PropTypes.func,
  setRowTicked: PropTypes.func,
  toggleList: PropTypes.func,
});

EntryList.propTypes = { bulkApply: BULK_APPLY_SHAPE.isRequired };

/**
 * The rows half of "Also apply": which rows, the System Deficit the Apply records, the rows
 * themselves and the facility note, as the items of one Stack inside the card.
 */
const BulkApplyRows = ({ bulkApply }) => (
  <>
    <ScopeOptions scope={bulkApply.scope} onChange={bulkApply.setScope} />
    <DeficitBanner
      entries={bulkApply.entries}
      lotNumber={bulkApply.lotNumber}
      isManualLot={bulkApply.isManualLot}
      editedRowDeficitQty={bulkApply.editedRowDeficitQty}
    />
    {/* A rule between the options and the rows they decide, so the two do not run together. */}
    <Stack spacing={1.5} sx={{ pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
      <EntryList bulkApply={bulkApply} />
      <ChangeNdcLotBulkApplyFacilityNote
        lotNumber={bulkApply.lotNumber}
        isManualLot={bulkApply.isManualLot}
        editedFacilityName={bulkApply.editedFacilityName}
        tickedRowsByOtherFacility={bulkApply.tickedRowsByOtherFacility}
        rowsLeftOutByFacility={bulkApply.rowsLeftOutByFacility}
      />
    </Stack>
  </>
);

BulkApplyRows.propTypes = { bulkApply: BULK_APPLY_SHAPE.isRequired };

const ChangeNdcLotBulkApply = ({ bulkApply, ndc }) => {
  const lotName = bulkApply.lotNumber ? `lot ${bulkApply.lotNumber}` : 'this lot';

  return (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Stack
        direction='row'
        alignItems='baseline'
        sx={{ gap: 1.25, mb: 1, minHeight: 32, flexWrap: 'wrap' }}
      >
        <Typography variant='subtitle1' component='h3' sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          Other rows with this NDC
        </Typography>
        <Typography variant='body2' sx={{ color: 'grey.800' }}>
          {'NDC '}
          <Box component='span' sx={{ fontFamily: 'monospace' }}>
            {ndc}
          </Box>
          {' · '}
          <Box component='span' sx={{ fontWeight: 500, color: 'text.primary' }}>
            {`${bulkApply.otherRowCount} more`}
          </Box>
          {' in this batch'}
        </Typography>
      </Stack>

      {/* One card: once ticked, what the tick decides opens inside it, under the checkbox. */}
      <Box
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
      >
        <Box sx={{ px: 1.5, py: 0.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={bulkApply.isApplied}
                onChange={(event) => bulkApply.setTicked(event.target.checked)}
              />
            }
            label={`Also apply ${lotName} to other selected rows with this NDC`}
          />
        </Box>

        {/* pl lines the rows up under the checkbox's label. */}
        {bulkApply.isApplied && (
          <Stack
            spacing={2.25}
            sx={{
              bgcolor: 'grey.50',
              py: 1.75,
              pr: 1.75,
              pl: 5.25,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <BulkApplyRows bulkApply={bulkApply} />
          </Stack>
        )}
      </Box>
    </Box>
  );
};

ChangeNdcLotBulkApply.propTypes = {
  bulkApply: BULK_APPLY_SHAPE.isRequired,
  /** The edited row's own NDC; only rows with this NDC are listed. */
  ndc: PropTypes.string.isRequired,
};

export default ChangeNdcLotBulkApply;
